export type StandaloneConnectStartResponse = {
  pending?: boolean;
  redirectUrl?: string;
  alreadyCompleted?: boolean;
};

function getApiUrl(): string {
  return `${import.meta.env.VITE_API_URL}`.replace(/\/$/, "");
}

export function getAuthRedirectUrl(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const record = payload as Record<string, unknown>;
  if (typeof record.redirectUrl === "string" && record.redirectUrl.trim()) {
    return record.redirectUrl.trim();
  }

  return getAuthRedirectUrl(record.data);
}

// Module-scoped cache: dedupes concurrent and repeated calls for the same
// externalAuthId across React remounts, which otherwise triggers a second
// /connect/login hit that resolves with `alreadyCompleted: true` and strips
// the query param before the first call's redirect can take effect.
const inFlightRequests = new Map<string, Promise<StandaloneConnectStartResponse>>();

async function performStartStandaloneConnect(externalAuthId: string): Promise<StandaloneConnectStartResponse> {
  const response = await fetch(
    `${getApiUrl()}/connect/login?external_auth_id=${encodeURIComponent(externalAuthId)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "include",
    },
  );

  const body = response.status === 204 ? {} : await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(typeof body?.message === "string" ? body.message : "Unable to start external sign-in");
  }

  return body as StandaloneConnectStartResponse;
}

export function startStandaloneConnect(externalAuthId: string): Promise<StandaloneConnectStartResponse> {
  const existing = inFlightRequests.get(externalAuthId);
  if (existing) {
    return existing;
  }

  const request = performStartStandaloneConnect(externalAuthId);
  inFlightRequests.set(externalAuthId, request);
  request.catch(() => {
    inFlightRequests.delete(externalAuthId);
  });
  return request;
}

export function completeAuthNavigation(payload: unknown): void {
  window.location.replace(getAuthRedirectUrl(payload) || "/applications");
}
